import React, { useState, useEffect } from 'react'
import { Box, Divider, Text } from '@deseretbook/react-ui'
import propTypes from 'prop-types'
import PageLayout from '../layouts/DefaultLayout'
import ErrorComponent from '../components/ErrorComponent'
import LoadingIndicator from '../components/media/loadingIndicator/LoadingIndicator'

import BreadcrumbButton from '../components/buttons/BreadcrumbButton'
import MediaCardFull from '../components/media/mediaCard/MediaCardFull'
import { getComponentById } from '../api/content/pages'
import { VIEW_OPTIONS } from '../components/media/constants'
import ViewOptions from '../components/media/mediaCard/ViewOptions'
import { BOOKSHELF_PLUS_IDS, DIGITAL_VIP_IDS } from '../subscriptionIds'
import checkMediaSubscription from '../utils/checkMediaSub'
import { MediaCard } from '../components/media'
import { Columns } from '../dbWebUI'

/**
 * Landing page for Media items
 *
 * @param {object} props - component props
 * @returns {function} Component
 */
const DiscoverListPage = ({ match }) => {
  const { id } = match.params
  const [pageData, setPageData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const defaultMediaView = localStorage.getItem('mediaView') || VIEW_OPTIONS.grid
  const [view, setView] = useState(defaultMediaView)

  const getPage = async () => {
    try {
      const response = await getComponentById(Number(id))
      setPageData(response)
      setIsLoading(false)
    } catch (error) {
      console.error('Error fetching discovery data:', error)
      setError(error)
    }
  }

  /**
   * Changes the current view
   * @param {string} newView View to use. Can be list or grid
   */
  const toggleView = (newView) => {
    localStorage.setItem('mediaView', newView)
    setView(newView)
  }

  useEffect(() => {
    if (isLoading) {
      getPage()
    }
    // eslint-disable-next-line
  }, [isLoading])

  useEffect(() => {
    localStorage.setItem('breadcrumb', 'discover')
  }, [])

  if (error) {
    return (
      <PageLayout>
        <ErrorComponent error={error} />
      </PageLayout>
    )
  }

  const plusIds = BOOKSHELF_PLUS_IDS
  const vipIds = DIGITAL_VIP_IDS

  const totalItems = ((pageData && pageData.dynamicItems) || []).length

  return (
    <PageLayout id="DiscoverListPageContainer">
      <Box isFullwidth alignItems="center" justifyContent="space-between" marginTop={1}>
        <Box alignItems="center" justifyContent="flex-start">
          <BreadcrumbButton />
          <Text
            style={{
              display: 'inline-block',
              marginLeft: '40px',
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 600,
              fontSize: '32px',
            }}
          >
            {pageData.header}
          </Text>
        </Box>
        <ViewOptions toggleView={toggleView} currentView={view} />
      </Box>
      <Divider
        style={{ boxShadow: 'none', borderBottom: '1px solid #d7d7d7', marginBottom: '48px' }}
      />

      {isLoading ? (
        <LoadingIndicator isRelative />
      ) : (
        <Box justifyContent="start">
          {view === VIEW_OPTIONS.list ? (
            <Box>
              {((pageData && pageData.dynamicItems) || []).map((mediaItem, i) => {
                const { mediaData = {} } = mediaItem
                const variantType = mediaData && mediaData.ebook ? 'ebook' : 'audiobook'
                const sku = mediaData[variantType] && mediaData[variantType].sku

                return (
                  <div key={`${mediaItem.mediaId}-${sku}-${i}`}>
                    <MediaCardFull
                      media={mediaData}
                      mediaType={variantType}
                      variantSku={sku}
                      isDiscover
                    />
                  </div>
                )
              })}
            </Box>
          ) : (
            <Columns isMobile isMultiline>
              {((pageData && pageData.dynamicItems) || []).map((mediaItem, i) => {
                const { mediaData = {}, sku = '' } = mediaItem
                const variantType = mediaData && mediaData.ebook ? 'ebook' : 'audiobook'
                const isPlus = checkMediaSubscription(mediaData[variantType], plusIds)
                const isVip = checkMediaSubscription(mediaData[variantType], vipIds)

                return (
                  <Columns.Column
                    key={`${mediaItem.mediaId}-${sku}-${i}`}
                    is6Mobile
                    is4Tablet
                    is3Desktop
                    is3Widescreen
                    is2Fullhd={totalItems > 6}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                    }}
                  >
                    <MediaCard
                      media={mediaData}
                      mediaType={variantType}
                      isPlus={isPlus}
                      isVip={isVip}
                      variantSku={sku}
                      isDiscover
                    />
                  </Columns.Column>
                )
              })}
            </Columns>
          )}
        </Box>
      )}
    </PageLayout>
  )
}

DiscoverListPage.defaults = {
  match: {
    params: {
      id: '',
    },
  },
}

DiscoverListPage.propTypes = {
  match: propTypes.shape({
    params: propTypes.shape({
      id: propTypes.string,
    }),
  }).isRequired,
}

export default DiscoverListPage
